/**
 * Simple in-memory request cache to prevent duplicate API calls
 * during initial render and within a short time window.
 * 
 * This helps reduce waterfall requests and duplicate fetches on dashboard load.
 */

const cache = new Map();

// Per-endpoint TTL configuration (in milliseconds)
const CACHE_TTL_CONFIG = {
  '/api/activities/': 5 * 60 * 1000, // 5 minutes
  '/api/auth/user/': 60 * 1000, // 60 seconds
  '/api/consent/current/': 60 * 1000, // 60 seconds
  '/api/set-csrf/': 30 * 1000, // 30 seconds (short, CSRF-sensitive)
  '/api/rest/v1/journeys/current/': 60 * 1000, // 60 seconds
  default: 5 * 1000, // 5 seconds default
};

/**
 * Get TTL for a specific URL
 */
function getTTL(url) {
  for (const [pattern, ttl] of Object.entries(CACHE_TTL_CONFIG)) {
    if (pattern !== 'default' && url.includes(pattern)) {
      return ttl;
    }
  }
  return CACHE_TTL_CONFIG.default;
}

/**
 * Get cache key from request config
 */
function getCacheKey(url, params = {}) {
  const sortedParams = Object.keys(params)
    .sort()
    .map(key => `${key}=${params[key]}`)
    .join('&');
  return `${url}?${sortedParams}`;
}

/**
 * Check if cached response is still valid
 */
function isCacheValid(entry, ttl) {
  return Date.now() - entry.timestamp < ttl;
}

/**
 * Wrapper for axios requests that deduplicates concurrent requests
 * and caches responses for a short time.
 * 
 * @param {Function} requestFn - The axios request function (e.g., api.get)
 * @param {string} url - The API endpoint URL
 * @param {object} config - Axios config (params, headers, etc.)
 * @returns {Promise} - The axios response
 */
export function cachedRequest(requestFn, url, config = {}) {
  const cacheKey = getCacheKey(url, config.params || {});
  const ttl = getTTL(url);
  const now = Date.now();
  
  // Check if we have a valid cached response
  if (cache.has(cacheKey)) {
    const entry = cache.get(cacheKey);
    if (isCacheValid(entry, ttl)) {
      // Return cached promise if still pending, or cached response
      if (entry.promise) {
        return entry.promise;
      }
      return Promise.resolve(entry.response);
    }
    // Cache expired, remove it
    cache.delete(cacheKey);
  }
  
  // Check if there's already an in-flight request for this URL
  const existingEntry = cache.get(cacheKey);
  if (existingEntry && existingEntry.promise) {
    return existingEntry.promise;
  }
  
  // Make the request
  const promise = requestFn(url, config)
    .then(response => {
      // Cache the successful response
      cache.set(cacheKey, {
        response,
        timestamp: now,
        promise: null
      });
      return response;
    })
    .catch(error => {
      // Don't cache errors, but clear the promise
      const entry = cache.get(cacheKey);
      if (entry) {
        entry.promise = null;
      }
      throw error;
    });
  
  // Store the promise while request is in flight
  cache.set(cacheKey, {
    response: null,
    timestamp: now,
    promise
  });
  
  return promise;
}

/**
 * Clear the cache (useful for testing or manual invalidation)
 */
export function clearCache() {
  cache.clear();
}

/**
 * Clear cache for a specific URL pattern
 */
export function clearCacheForUrl(urlPattern) {
  for (const key of cache.keys()) {
    if (key.startsWith(urlPattern)) {
      cache.delete(key);
    }
  }
}

